/**
 * Site Plan Highlight Plugin for Search Results Map
 *
 * Subscribes to the `listing$` Observable
 * and emits sitePlanHovered$ / sitePlanHoverEnded$ when the focused listing
 * corresponds to an interactive site-map community visible at the current zoom.
 *
 * Depends on `context.services.mapFacade` and `context.services.getState` being present.
 *
 * Single responsibility: bridge list-view focus signals into the
 * sitePlanHovered$ / sitePlanHoverEnded$ observables so that
 * sitePlanMiniCard.plugin can react without any coupling to this plugin.
 *
 * This plugin does NOT:
 * - Handle marker highlighting or transient markers
 * - Run on mobile (see sitePlanCarousel.plugin)
 * - Render or dismiss the mini card (see sitePlanMiniCard.plugin)
 * - Manage site plan overlay lifecycles (see sitePlan.plugin, interactiveSitePlan.plugin)
 * - Know how the focus signal originates — it only consumes the Observable
 *
 * Reason to change: ISM detection logic, zoom threshold for hover activation,
 * or the bridge between list-view focus and sitePlanHovered$ / sitePlanHoverEnded$.
 *
 * @module searchResultsMap/plugins/sitePlanHighlight.plugin
 */

import { filter } from 'rxjs';
import { sitePlanHovered$, sitePlanHoverEnded$ } from '../redux/observables';
import { isPointInBounds } from '../services/geometry';
import resolveFocusedMapResult from '../services/resolveFocusedMapResult';
import { SITE_PLAN_RENDER_ZOOM_THRESHOLD } from '../constants';

/**
 * Creates the sitePlanHighlight plugin.
 *
 * @param {Observable<{ id: string, communityId: string } | null>} listing$
 *   Read-only stream of focused listing changes. Provided by
 *   `services/listingFocus.js` — already deduplicated at the source.
 * @returns {{ name: string, init: Function, start: Function, stop: Function }}
 */
export default function createSitePlanHighlightPlugin(listing$) {
  const pluginState = {
    started: false,
    mapFacade: null,
    isDesktopBreakpoint: null,
    getState: null,
    hoveredCommunityId: null,
    subscriptions: [],
  };

  function clearSitePlanHover() {
    if (pluginState.hoveredCommunityId) {
      sitePlanHoverEnded$.next({ communityId: pluginState.hoveredCommunityId });
      pluginState.hoveredCommunityId = null;
    }
  }

  /**
   * Reacts to a focused listing change from the `listing$` stream.
   *
   * Clears any active site-plan hover signal, then — if the resolved listing
   * is an interactive site-map community that is in bounds and within the
   * zoom threshold — emits sitePlanHovered$ so sitePlanMiniCard can react.
   *
   * @param {{ id: string, communityId: string } | null} focusedListing
   */
  function handleFocusedListingChange(focusedListing) {
    clearSitePlanHover();

    if (!focusedListing?.id && !focusedListing?.communityId) {
      return;
    }

    const mapResult = resolveFocusedMapResult(focusedListing, pluginState.getState());
    if (!mapResult) {
      return;
    }

    if (!mapResult.hasInteractiveSiteMap || !mapResult.communityId) {
      return;
    }

    const inBounds = isPointInBounds(mapResult.position, pluginState.mapFacade?.getBounds?.());
    if (!inBounds) {
      return;
    }

    const snapshot = pluginState.mapFacade?.getCameraSnapshot?.() ?? null;
    const zoom = snapshot?.zoom ?? 0;

    if (zoom < SITE_PLAN_RENDER_ZOOM_THRESHOLD) {
      return;
    }

    pluginState.hoveredCommunityId = mapResult.communityId;
    sitePlanHovered$.next({ communityId: mapResult.communityId });
  }

  function setupSubscriptions() {
    pluginState.subscriptions.push(
      listing$.pipe(
        filter(() => pluginState.isDesktopBreakpoint?.() ?? false),
      ).subscribe(handleFocusedListingChange),
    );
  }

  function clearSubscriptions() {
    pluginState.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    pluginState.subscriptions = [];
  }

  return {
    name: 'site-plan-highlight',

    async init(context) {
      context.state.sitePlanHighlight = { started: false };
    },

    async start(context) {
      if (pluginState.started) {
        return;
      }

      pluginState.mapFacade = context.services.mapFacade;
      pluginState.isDesktopBreakpoint = context.services.isDesktopBreakpoint;
      pluginState.getState = context.services.getState;

      setupSubscriptions();

      pluginState.started = true;
      context.state.sitePlanHighlight = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      clearSitePlanHover();

      pluginState.mapFacade = null;
      pluginState.isDesktopBreakpoint = null;
      pluginState.getState = null;
      pluginState.started = false;

      delete context.state.sitePlanHighlight;
    },
  };
}
